import React, { useContext, useEffect, useState } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge, makeStyles, Tooltip } from "@material-ui/core";
import PublicIcon from "@material-ui/icons/Public";
import ViewWeekOutlinedIcon from "@material-ui/icons/ViewWeekOutlined";
import AndroidOutlinedIcon from "@material-ui/icons/AndroidOutlined";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

const useStyles = makeStyles((theme) => ({
  listItem: {
    minHeight: 48,
    margin: "6px 8px",
    width: "calc(100% - 16px)",
    padding: "10px 8px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(0,0,0,.08)",
    transition: "background-color .2s, border-color .2s",
    "&.Mui-selected, &.Mui-selected:hover": {
      background: "#e5f7ed", color: "#0c5135", borderColor: "#b3e4ca",
      "& $icon, & $text": { color: "#0c5135" },
      "& $text": { fontWeight: 700 },
    },
    "&.Mui-focusVisible": { outline: "3px solid #ffc667", outlineOffset: -3 },
    color: "#ffffff", // Garante texto branco para o degradê
    "&:hover": {
      // Efeito de destaque leve ao passar o mouse
      backgroundColor: "rgba(255, 255, 255, 0.15)", 
    },
  },
  icon: {
    minWidth: 32,
    color: "#ffffff", // Ícones brancos para contraste
  },
  text: {
    color: "#ffffff", 
    fontWeight: "500",
  },
  subheader: {
    color: "#ffffff",
    // Removido o verde sólido para o degradê do fundo aparecer
    backgroundColor: "transparent", 
    fontWeight: "bold",
    opacity: 0.8,
  },
  divider: {
    // Divisória sutil para não brigar com o degradê
    backgroundColor: "rgba(255, 255, 255, 0.12)", 
  },
  badge: {
    backgroundColor: "#25D366", // Verde ZapTicket no badge
    color: "#fff",
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, className } = props;
  const classes = useStyles();
  const { pathname } = useLocation();
  const selected = to === "/" ? pathname === "/" : pathname.toLowerCase().startsWith(to.toLowerCase());

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <Tooltip title={primary} placement="right" arrow>
      <ListItem 
        button
        selected={selected}
        role="link"
        aria-current={selected ? "page" : undefined}
        aria-label={primary}
        component={renderLink} 
        className={clsx(className, classes.listItem)}
      >
        {icon ? <ListItemIcon className={classes.icon}>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} classes={{ primary: classes.text }} />
      </ListItem>
      </Tooltip>
    </li>
  );
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps && whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(whats.status);
        });
        setConnectionWarning(offlineWhats.length > 0);
      } else { setConnectionWarning(false); }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink to="/" primary="Dashboard" icon={<DashboardOutlinedIcon />} />
      <ListItemLink to="/tickets" primary={i18n.t("mainDrawer.listItems.tickets")} icon={<WhatsAppIcon />} />
      {user.rplChatEnabled && <ListItemLink to="/rpl-chat" primary="Chat do site RPL" icon={<PublicIcon />} />}
      <ListItemLink to="/contacts" primary={i18n.t("mainDrawer.listItems.contacts")} icon={<ContactPhoneOutlinedIcon />} />
      <ListItemLink to="/quickAnswers" primary={i18n.t("mainDrawer.listItems.quickAnswers")} icon={<QuestionAnswerOutlinedIcon />} />
      
      <ListItemLink to="/settings" primary={i18n.t("mainDrawer.listItems.settings")} icon={<Badge badgeContent={connectionWarning ? "!" : 0} color="error"><SettingsOutlinedIcon /></Badge>} />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider className={classes.divider} />
            <ListSubheader inset className={classes.subheader}>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink to="/kanban" primary="Kanban" icon={<ViewWeekOutlinedIcon />} />
            <ListItemLink to="/chatbot" primary="Chatbot" icon={<AndroidOutlinedIcon />} />
            <ListItemLink to="/users" primary={i18n.t("mainDrawer.listItems.users")} icon={<PeopleAltOutlinedIcon />} />
            <ListItemLink to="/queues" primary={i18n.t("mainDrawer.listItems.queues")} icon={<AccountTreeOutlinedIcon />} />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
